/* eslint-disable no-nested-ternary -- verbatim backup of the pre-redesign page; kept faithful. */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc } from 'firebase/firestore';
import { getAuth, updateEmail as firebaseUpdateEmail } from 'firebase/auth';
import { MdSave, MdEmail, MdPowerSettingsNew } from 'react-icons/md';
import PulseLoader from 'react-spinners/PulseLoader';

import { useAuth } from '~/services/auth';
import { logEvent, db } from '~/config/firebase.config';
import { StripeServerForm } from '~/components/billing/StripeServerForm';
import { useSyncClient } from '~/hooks/models/useClient';
import { Button } from '~/components/ui/button';
import { NavbarContainer } from '~/components/navigation/NavbarContainer';
import { Input } from '~/components/ui/input';
import { Select } from '~/components/ui/select';
import { useSyncUser } from '~/hooks/models/useUser';
import { Alert } from '~/components/ui/alert';

import styles from './Account.module.css';
import { LoadingPage } from '../LoadingPage';

// Legacy My Account page — kept as a backup of the pre-redesign UI.
// Reachable at /account/legacy for rollback/visual comparison while the
// redesigned Account (./Account.tsx) is validated. Logic unchanged.
export function AccountLegacy() {
    const { state } = useLocation();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const client = useSyncClient(state?.clientID);
    const { auth, sendResetLink } = useAuth();
    const user = useSyncUser(auth?.uid || '');

    const [form, setForm] = useState({ firstName: '', lastName: '', email: '', companyName: '', lang: i18n.language });
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sendResetPasswordEmail, setSendResetPasswordEmail] = useState(false);
    const [successSend, setSuccessSend] = useState(false);

    const { logout } = useAuth();

    const handleLogout = () => {
        logEvent('account_logout_click', state?.clientID === 'demo' ? 'Demo' : 'Home');
        logout();
        if (state?.clientID === 'demo') {
            localStorage.removeItem('clientID');
            navigate('/signin');
        }
    };

    useEffect(() => {
        logEvent('screen_view', 'Account');
    }, []);

    useEffect(() => {
        if (user) {
            setForm((prev) => ({
                ...prev,
                firstName: (user as any)?.firstname || '',
                lastName: (user as any)?.lastname || '',
                email: (user as any)?.email || auth?.email || '',
            }));
        }
    }, [user, auth?.email]);

    useEffect(() => {
        if (client) {
            setForm((prev) => ({
                ...prev,
                companyName: (client as any)?.name || '',
                lang: (client as any)?.language || i18n.language,
            }));
        }
    }, [client]);

    const canSave = useMemo(
        () =>
            !!auth?.uid &&
            (form.firstName.trim() !== '' ||
                form.lastName.trim() !== '' ||
                form.email.trim() !== '' ||
                form.companyName.trim() !== ''),
        [auth?.uid, form]
    );

    const handleResetPassword = async () => {
        const authTmp = getAuth();
        authTmp.languageCode = i18n.language;
        setSendResetPasswordEmail(true);
        setSuccessSend(false);
        const targetEmail = form.email || auth?.email || '';
        try {
            await sendResetLink(authTmp, targetEmail);
            setSendResetPasswordEmail(false);
            setSuccessSend(true);
            setTimeout(() => setSuccessSend(false), 2000);
        } catch (err) {
            console.error('Error sending reset password email:', err);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!auth?.uid) return;
        setSaving(true);
        setSuccess(false);
        setError(null);
        try {
            const userRef = doc(db, 'users', auth.uid);
            const updates: Record<string, any> = {};
            if (form.firstName.trim() !== '') updates.firstname = form.firstName.trim();
            if (form.lastName.trim() !== '') updates.lastname = form.lastName.trim();

            if (Object.keys(updates).length > 0) {
                await updateDoc(userRef, updates);
            }

            const currentEmail = (user as any)?.email || auth?.email || '';
            const emailChanged = form.email.trim() !== '' && form.email.trim() !== currentEmail;
            if (emailChanged) {
                const authInstance = getAuth();
                if (authInstance.currentUser) {
                    await firebaseUpdateEmail(authInstance.currentUser, form.email.trim());
                    await updateDoc(userRef, { email: form.email.trim() });
                }
            }

            // Update client document (company name and language)
            if (state?.clientID) {
                const clientRef = doc(db, 'clients', state.clientID);
                const clientUpdates: Record<string, any> = {};

                const currentCompany = (client as any)?.name || '';
                if (form.companyName.trim() !== '' && form.companyName.trim() !== currentCompany) {
                    clientUpdates.name = form.companyName.trim();
                }

                const currentLang = (client as any)?.language || '';
                if (form.lang && form.lang !== currentLang) {
                    clientUpdates.lang = form.lang;
                    i18n.changeLanguage(form.lang); // `<html lang>` suit via main.tsx
                }

                if (Object.keys(clientUpdates).length > 0) {
                    await updateDoc(clientRef, clientUpdates);
                }
            }
            setSuccess(true);
            setTimeout(() => setSuccess(false), 2000);
        } catch (err: any) {
            const code = err?.code || '';
            if (code === 'auth/requires-recent-login' || code === 'CREDENTIAL_TOO_OLD_LOGIN_AGAIN') {
                setError(t('account.reauthPrompt'));
            } else if (code === 'auth/email-already-in-use') {
                setError(t('account.emailInUse'));
            } else {
                setError(t('account.updateFailed'));
            }
        } finally {
            setSaving(false);
        }
    };

    if (!client) {
        return <LoadingPage>{t('home.loadingClient')}</LoadingPage>;
    }

    return (
        <>
            <NavbarContainer>
                <div />
                <Button preset="secondary" onClick={() => navigate('/home')}>
                    {t('global.close')}
                </Button>
            </NavbarContainer>
            <div className={styles.content}>
                <h1>
                    {t('home.hello')} <span className="emph">{client?.name}</span> ✌️
                </h1>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', width: '100%' }}>
                    <form
                        onSubmit={handleSave}
                        style={{
                            width: '80%',
                            gap: 'var(--spacing-l)',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <div style={{ display: 'flex', gap: 'var(--spacing-l)' }}>
                            <div style={{ flex: 1 }}>
                                <Input
                                    id="firstName"
                                    containerClassName={styles.margin}
                                    value={form.firstName}
                                    onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))}
                                >
                                    {t('account.firstName')}
                                </Input>
                            </div>
                            <div style={{ flex: 1 }}>
                                <Input
                                    id="lastName"
                                    containerClassName={styles.margin}
                                    value={form.lastName}
                                    onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))}
                                >
                                    {t('account.lastName')}
                                </Input>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 'var(--spacing-l)' }}>
                            <div style={{ flex: 1 }}>
                                <Input
                                    id="companyName"
                                    containerClassName={styles.margin}
                                    value={form.companyName}
                                    onChange={(e) => setForm((prev) => ({ ...prev, companyName: e.target.value }))}
                                >
                                    {t('account.companyName')}
                                </Input>
                            </div>
                            <div style={{ flex: 1 }}>
                                <Select
                                    id="lang"
                                    containerClassName={styles.margin}
                                    value={{
                                        value: form.lang,
                                        label: t(`global.infos.input.langOptions.${form.lang}`),
                                    }}
                                    onChange={(option: any) => {
                                        if (option) {
                                            setForm((prev) => ({ ...prev, lang: option.value }));
                                        }
                                    }}
                                    options={[
                                        { value: 'en', label: t('global.infos.input.langOptions.en') },
                                        { value: 'fr', label: t('global.infos.input.langOptions.fr') },
                                        { value: 'es', label: t('global.infos.input.langOptions.es') },
                                        { value: 'de', label: t('global.infos.input.langOptions.de') },
                                    ]}
                                >
                                    {t('account.lang')}
                                </Select>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 'var(--spacing-l)' }}>
                            <div style={{ flex: 1 }}>
                                <Input
                                    id="email"
                                    containerClassName={styles.margin}
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                                >
                                    {t('account.email')}
                                </Input>
                            </div>
                            <div
                                style={{ flex: 1, display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}
                            >
                                <label htmlFor="resetPassword" style={{ marginRight: 'var(--spacing-s)' }}>
                                    {t('global.login.input.password')}
                                </label>
                                <Button
                                    type="button"
                                    preset={successSend ? 'success' : 'secondary'}
                                    style={{ width: '94%' }}
                                    onClick={handleResetPassword}
                                    icon={<MdEmail />}
                                    disabled={sendResetPasswordEmail}
                                >
                                    {sendResetPasswordEmail ? (
                                        <PulseLoader color="#FFFFFF" size={8} aria-label="Loading Spinner" />
                                    ) : successSend ? (
                                        t('account.emailSuccess')
                                    ) : (
                                        t('account.resetPassword')
                                    )}
                                </Button>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 'var(--spacing-l)' }}>
                            <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    type="submit"
                                    disabled={!canSave || saving}
                                    icon={<MdSave />}
                                    preset={success ? 'success' : 'primary'}
                                >
                                    {saving ? (
                                        <PulseLoader color="#FFFFFF" size={8} aria-label="Loading Spinner" />
                                    ) : success ? (
                                        t('account.updated')
                                    ) : (
                                        t('account.save')
                                    )}
                                </Button>
                            </div>
                        </div>{' '}
                    </form>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    {client?.stripeCustomerId && (
                        <StripeServerForm
                            className={styles.form}
                            route="/create-customer-portal-session"
                            clientId={state?.clientID || ''}
                            lang={i18n.language}
                        >
                            <Button type="submit" preset="secondary">
                                {t('account.manageSubscription')}
                            </Button>
                        </StripeServerForm>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}

                    <Button onClick={handleLogout} icon={<MdPowerSettingsNew />}>
                        {t('home.logout')}
                    </Button>
                </div>
            </div>
        </>
    );
}
